import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import PostJobPage from '@/pages/PostJobPage';
import MyJobsPage from '@/pages/MyJobsPage';
import ArtisanFeedPage from '@/pages/ArtisanFeedPage';
import JobDetailPage from '@/pages/JobDetailPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPage from '@/pages/AdminPage';
import QuickQuotePage from '@/pages/QuickQuotePage';
import ChatPage from '@/pages/ChatPage';
import ConversationsPage from '@/pages/ConversationsPage';
import WalletPage from '@/pages/WalletPage';
import PaymentCallbackPage from '@/pages/PaymentCallbackPage';
import VettingPage from '@/pages/VettingPage';
import NotificationsPage from '@/pages/NotificationsPage';
import MilestonesPage from '@/pages/MilestonesPage';
import ProgressTrackerPage from '@/pages/ProgressTrackerPage';
import RevenueDashboardPage from '@/pages/RevenueDashboardPage';
import PortfolioPage from '@/pages/PortfolioPage';
import BrowsePortfoliosPage from '@/pages/BrowsePortfoliosPage';
import '@/App.css';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#E5E3DB] border-t-[#E86A33] rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/quick-quote" element={<QuickQuotePage />} />
      <Route path="/payment/callback" element={<PaymentCallbackPage />} />
      <Route path="/post-job" element={<ProtectedRoute roles={['customer']}><PostJobPage /></ProtectedRoute>} />
      <Route path="/my-jobs" element={<ProtectedRoute roles={['customer']}><MyJobsPage /></ProtectedRoute>} />
      <Route path="/feed" element={<ProtectedRoute roles={['artisan', 'facility_manager']}><ArtisanFeedPage /></ProtectedRoute>} />
      <Route path="/job/:id" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
      <Route path="/chat/:jobId/:userId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/vetting" element={<ProtectedRoute roles={['artisan', 'facility_manager']}><VettingPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/milestones/:jobId" element={<ProtectedRoute><MilestonesPage /></ProtectedRoute>} />
      <Route path="/progress/:jobId" element={<ProtectedRoute><ProgressTrackerPage /></ProtectedRoute>} />
      <Route path="/admin/revenue" element={<ProtectedRoute roles={['admin']}><RevenueDashboardPage /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute roles={['artisan', 'facility_manager']}><PortfolioPage /></ProtectedRoute>} />
      <Route path="/portfolios" element={<BrowsePortfoliosPage />} />
      <Route path="/portfolios/:artisanId" element={<BrowsePortfoliosPage />} />
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
